/** Back and forward beside the desktop search, as in the cloud shell. A page's own Back (its
 * published header back action) takes the Back arrow; otherwise both follow the browser's own
 * history, so a page's unsaved-changes guard is asked exactly as it is for the browser's Back. */
import { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight } from '@/shared/ui/icons';
import type { BrowserHeaderBackAction } from './browserHeaderBackAction';
import { useDesktopFrameTouch } from './desktopFrame';

interface NavigationLike extends EventTarget {
  canGoBack?: boolean;
  canGoForward?: boolean;
}

function browserNavigation(): NavigationLike | null {
  const nav = (window as unknown as { navigation?: NavigationLike }).navigation;
  return nav && typeof nav.addEventListener === 'function' ? nav : null;
}

function readHistory(): { back: boolean; forward: boolean } {
  const nav = browserNavigation();
  // Without the Navigation API there is no way to ask, so leave both usable; the browser ignores
  // a Back or Forward with nowhere to go.
  return {
    back: nav?.canGoBack ?? true,
    forward: nav?.canGoForward ?? true,
  };
}

export function DesktopHistoryControls({ backAction = null }: { backAction?: BrowserHeaderBackAction | null }) {
  // A tablet has no hover and keeps its header lean: Back only, the room goes to the page title.
  const showForward = !useDesktopFrameTouch();
  const [state, setState] = useState(readHistory);
  useEffect(() => {
    const nav = browserNavigation();
    const update = () => setState(readHistory());
    nav?.addEventListener('currententrychange', update);
    window.addEventListener('popstate', update);
    return () => {
      nav?.removeEventListener('currententrychange', update);
      window.removeEventListener('popstate', update);
    };
  }, []);

  return (
    <div data-slot="desktop-history-controls" role="group" aria-label="View history" className="flex items-center gap-0.5">
      <button type="button" aria-label={backAction?.label ?? 'Go back'} title={backAction?.label ?? 'Go back'}
        disabled={!backAction && !state.back}
        onClick={() => (backAction ? backAction.onClick() : window.history.back())}>
        <ArrowLeft size={18} aria-hidden="true" focusable="false" />
      </button>
      {showForward && (
        <button type="button" aria-label="Go forward" title="Go forward" disabled={!state.forward}
          onClick={() => window.history.forward()}>
          <ArrowRight size={18} aria-hidden="true" focusable="false" />
        </button>
      )}
    </div>
  );
}
